import {
  LessonComponentResponseType,
  SorterComponent
} from '../../../models/LessonComponent';
import React, { useEffect, useState } from 'react';
import {
  SorterCategory,
  SorterItem
} from '../../../models/LessonComponentContent';

import Grid from '@mui/material/Unstable_Grid2';
import LessonComponentService from '../../../services/LessonComponentService';
import LessonResponseService from '../../../services/LessonResponseService';
import { SorterCategoryView } from './SorterCategoryView';
import { cloneDeep } from 'lodash';
import { useLessonComponentContext } from '../../../models/LessonComponentContext';
import { useLessonContext } from '../../../models/LessonContext';
import { useTheme } from '@mui/material/styles';

interface SorterViewProps {
  component: SorterComponent;
  response: SorterCategory[];
  onChange: (response: LessonComponentResponseType) => void;
}

export function SorterView(props: SorterViewProps) {
  const { lessonType } = useLessonContext();
  const { reattempt } = useLessonComponentContext();
  const muiTheme = useTheme();
  const primaryColor = muiTheme.palette.primary.main;

  /** All items from the different categories, randomized. */
  const [allItems, setAllItems] = useState<SorterItem[]>([]);
  /** Items remaining in the Source pile. */
  const [sourceItems, setSourceItems] = useState<SorterItem[]>([]);
  /** Categories of items sorted by user. */
  const [categories, setCategories] = useState<SorterCategory[]>([]);

  /** Returns a shallow copied flat list of items from the categories. */
  const getSourceItems = (categories: SorterCategory[]) =>
    !categories
      ? []
      : [].concat(...categories?.map((c) => [].concat(...c.items)));

  const contentCategories = props.component.content.categories;
  useEffect(() => {
    const items = getSourceItems(contentCategories);
    LessonComponentService.randomizeItems(items);
    setAllItems(items);
  }, [contentCategories]);

  useEffect(() => {
    setCategories(
      contentCategories.map((category) => ({
        ...category,
        items: props.response?.find((c) => c.id === category.id)?.items || []
      }))
    );

    const sortedSet = new Set(getSourceItems(props.response).map((i) => i.id));
    const sourceSet = new Set(sourceItems.map((i) => i.id));
    setSourceItems(
      sourceItems
        .filter((i) => !sortedSet.has(i.id))
        .concat(
          ...allItems.filter(
            (i) => !sortedSet.has(i.id) && !sourceSet.has(i.id)
          )
        )
    );
  }, [allItems, props.response]);

  useEffect(() => {
    if (reattempt && lessonType === 'lesson') {
      //* In the 'Basic' type, return wrong items to source pile.
      moveWrongAnswersToSource();
    }
  }, [reattempt]);

  const moveWrongAnswersToSource = () => {
    const wrongAnswers = LessonResponseService.getSorterWrongAnswers(
      categories,
      props.component.content.correctResponsePattern
    );
    for (const wrongAnswer of wrongAnswers) {
      const categoryWithWrongAnswer = categories.find(
        (category) => category.id === wrongAnswer.categoryId
      );
      if (!categoryWithWrongAnswer) {
        //* must exist.
        throw new Error(`Category ${wrongAnswer.categoryId} not found.`);
      }
      const wrongAnswerItem = categoryWithWrongAnswer.items.find(
        (item) => item.id === wrongAnswer.itemId
      );
      returnItemToSource(wrongAnswer.categoryId, wrongAnswerItem);
    }
  };

  const returnItemToSource = (categoryId: string, item: SorterItem) => {
    setCategories((categories) => {
      const copy = cloneDeep(categories);
      const targetCategory = copy.find((c) => c.id === categoryId);
      if (!targetCategory) {
        throw new Error(`Target category ${categoryId} not found`);
      }
      const itemIndex = targetCategory.items.findIndex((i) => i.id === item.id);
      if (itemIndex < 0) {
        throw new Error(`Item ${item.id} not found.`);
      }
      targetCategory.items.splice(itemIndex, 1);
      return copy;
    });
    setSourceItems((items) => {
      const copy = cloneDeep(items);
      copy.unshift({ ...item });
      return copy;
    });
  };

  const handleMoveItems = (categories: SorterCategory[]) => {
    setCategories(categories);
    props.onChange(categories);
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid>
        <Grid container direction="column">
          <SorterCategoryView
            displayMode={props.component?.settings.sorterDisplayMode}
            componentkey={props.component?.componentkey}
            categories={categories}
            sourceItems={sourceItems}
            onMoveItems={handleMoveItems}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
